'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { Focusable } from '@ariakit/react';
import { forwardRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { fromMd, timingDefault, withHoverSupport } from '../../constants';
import { focusOutline } from '../../utility-classes';
import { TypeStyle } from '../TypeStyle';
import { extractButtonColorsByVariant } from './utils';
export function buttonSizeToHeight(size) {
    const mapping = {
        large: 50,
        medium: 42,
        small: 32,
    };
    return `${mapping[size]}px`;
}
export const StyledButton = styled.button `
  ${TypeStyle.css.eyebrow};
  ${focusOutline};

  ${({ $allCaps = true, $block = false, $size = 'medium', $borderless = false, $flushLeft, $flushRight, $variantColors, ...rest }) => {
    const isDisabled = rest.disabled || rest['aria-disabled'];
    const { normal: normalColors, hover: hoverColors, disabled: disabledColors } = $variantColors;
    const colors = isDisabled ? disabledColors : normalColors;
    return `
      align-items: center;
      background-color: ${colors.backgroundColor};
      border-color: ${$borderless ? 'transparent' : colors.borderColor};
      border-radius: 4px;
      border-style: solid;
      border-width: 2px;
      color: ${colors.color};
      cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
      display: ${$block ? 'flex' : 'inline-flex'};
      font-size: ${$size === 'small' ? '11px' : '12px'};
      justify-content: center;
      min-height: ${buttonSizeToHeight($size)};
      padding: 0 32px;
      ${$flushLeft ? 'margin-left: -32px;' : null};
      ${$flushRight ? 'margin-right: -32px;' : null};
      text-transform: ${$allCaps ? 'uppercase' : null};
      transition:
        background-color ${timingDefault},
        border-color ${timingDefault},
        color ${timingDefault};
      ${$block
        ? `
        width: 100%;
        width: -moz-available;
        width: -webkit-fill-available;
        width: fill-available;
        `
        : null};
      position: relative;

      ${fromMd} {
        font-size: 12px;
      }

      ${withHoverSupport} {
        &:hover {
          background-color: ${!isDisabled ? hoverColors.backgroundColor : null};
          border-color: ${isDisabled || $borderless ? 'transparent' : hoverColors.borderColor};
          color: ${!isDisabled ? hoverColors.color : null};
        }
      }
    `;
}}
`;
export const Button = forwardRef(({ allCaps, 'aria-disabled': ariaDisabled = false, accessibleWhenDisabled = true, block, disabled = false, size, variant = 'filled', borderless, flushBottom, flushLeft, flushRight, flushTop, customVariants, ...rest }, ref) => {
    const theme = useTheme();
    const variantColors = extractButtonColorsByVariant({
        theme,
        variant,
        customVariants,
    });
    return (_jsx(Focusable, { render: _jsx(StyledButton, { ...rest, ref: ref, "$allCaps": allCaps, "$block": block, "$size": size, "$variant": variant, "$borderless": borderless, "$flushBottom": flushBottom, "$flushLeft": flushLeft, "$flushRight": flushRight, "$flushTop": flushTop, "$variantColors": variantColors }), disabled: Boolean(disabled || ariaDisabled), accessibleWhenDisabled: accessibleWhenDisabled }));
});
//# sourceMappingURL=Button.js.map